import React, { useCallback } from 'react';
import InputNumberFormat from 'react-number-format';

// import { Warning as WarningIcon } from '@styled-icons/entypo/Warning';
import InfoIcon from 'mdi-react/InfoCircleOutlineIcon';

import {
  Box,
  Icon,
  Td,
  Text,
  Input,
  Flex,
  // Tooltip,
  Tr,
} from '@chakra-ui/react';

import { Row, FlattenedComponent } from './types';

export type Input = {
  price: number;
  newQuantity: number;
  recursive_quantity: number;
  bdi_percent: number;
  input_price: {
    custom_description: string | null;
    base: {
      description: string;
    };
    version: {
      description: string;
    };
    input: {
      code: string;
      description: string;
      version: {
        year?: number;
        month?: number;
        description: string;
        base: {
          description: string;
        };
      };
      unit_measure: {
        description: string;
      };
    };
  };
};

type Item = FlattenedComponent & Input;

interface RowProps {
  row: Row & { original: Item };
  data: FlattenedComponent[];
  onUpdateComponentBDI: (id: number, bdi: number | undefined) => void;
}

const InputRow: React.FC<RowProps> = ({ row, data, onUpdateComponentBDI }) => {
  const itemAppearsInMoreThanOneLocation = useCallback(
    (item: Item) => {
      return (
        data.filter(
          (i) => i.input_price?.input.code === item.input_price.input.code,
        ).length > 1
      );
    },
    [data],
  );

  const getAllLocations = useCallback(
    (item: Item) => {
      return data
        .filter(
          (i) => i.input_price?.input.code === item.input_price.input.code,
        )
        .map((i) => i.location);
    },
    [data],
  );

  return (
    <Tr {...row.getRowProps()}>
      <Td>
        <Box
          display="flex"
          flexDir="column"
          ml="1"
          justifyContent="center"
          alignItems="center"
          fontWeight="bold"
        >
          {row.original.location}
          <Icon w={5} h={5} as={InfoIcon} />
        </Box>
      </Td>

      <Td title="Base / Referência">
        <Text textAlign="center">
          {row.original.input_price.base?.description}{' '}
        </Text>

        <Text
          textAlign="center"
          whiteSpace="pre-wrap"
          maxWidth={{ base: '100%', md: '650px' }}
        >
          {row.original.input_price.version?.description ||
            row.original.input_price.input?.version?.description}
        </Text>
      </Td>

      <Td>{row.original.input_price.input.code}</Td>
      <Td>
        <Text
          py={1}
          whiteSpace="pre-wrap"
          maxWidth={{ base: '100%', md: '650px' }}
        >
          {row.original.input_price.custom_description ||
            row.original.input_price.input.description}
        </Text>

        {itemAppearsInMoreThanOneLocation(row.original) && (
          <Text
            fontSize="xs"
            fontWeight="bold"
            color="gray.700"
            whiteSpace="pre-wrap"
            maxWidth={{ base: '100%', md: '650px' }}
          >
            Esse insumo está presente nas posições:{' '}
            {getAllLocations(row.original).join(', ')} do orçamento
          </Text>
        )}
      </Td>

      <Td isNumeric>
        {Intl.NumberFormat('pt-BR', {
          maximumFractionDigits: 4,
        }).format(row.original.quantity)}
      </Td>

      <Td isNumeric>
        <Flex gap={2} alignItems="center" justifyContent="flex-end">
          <Text>
            {Intl.NumberFormat('pt-BR', {
              style: 'currency',
              currency: 'BRL',
            }).format(row.original.social_law_price)}
          </Text>

          {/* {row.original.outdated_price && (
            <Tooltip
              ml={1}
              hasArrow
              label="Insumo com preço desatualizado em relação a sua versão original."
            >
              <Icon as={WarningIcon} className="icon" />
            </Tooltip>
          )} */}
        </Flex>
      </Td>

      <Td isNumeric>
        {Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL',
        }).format(row.original.price)}
      </Td>

      <Td isNumeric>
        <InputNumberFormat
          type="tel"
          customInput={Input}
          displayType="input"
          className="text-right"
          placeholder="0 %"
          fixedDecimalScale
          thousandSeparator="."
          decimalSeparator=","
          suffix=" %"
          decimalScale={2}
          value={row.original.bdi_percent}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              const currentElement = e.target as HTMLInputElement;

              const inputs = document.querySelectorAll('input');
              const currentIndex = Array.from(inputs).indexOf(currentElement);

              if (currentIndex !== -1 && currentIndex < inputs.length - 1) {
                const nextElement = inputs[currentIndex + 1];
                nextElement.focus();
              }
            }
          }}
          onFocus={(e) => e.target.select()}
          onValueChange={(v) => {
            onUpdateComponentBDI(row.original.id, v.floatValue);
          }}
        />
      </Td>

      <Td isNumeric>
        {Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL',
        }).format(row.original.total)}
      </Td>
    </Tr>
  );
};

export default InputRow;
